import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  OnDestroy
} from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { Dictionary } from '@ngrx/entity';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NgxImageCompressService, DOC_ORIENTATION } from 'ngx-image-compress';
import { get } from 'lodash';
import { Answers, IFile } from '@care-portals/api-interfaces';
import { FileFacade } from '../../../+store/file/file.facade';
import { takeWhile } from 'rxjs';

@Component({
  selector: 'my-health-admin-answer',
  templateUrl: './answer.component.html',
  styleUrls: ['./answer.component.less'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class AdminAnswerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data;
  @Input() field: string;
  @Input() question;
  @Input() answer;
  @Input() allAnswers;
  @Input() editMode: boolean;
  @Input() answers: Answers;
  @Input() brand: string;
  @Input() customerId: string;
  optionAnswers = {};
  hideLabel = false;
  transferRxFileName: string;
  transferRxFile: IFile;
  active = true;

  files: Dictionary<IFile> = {};
  images: { name: string; path: string; loading: boolean }[] = [];

  constructor(
    private message: NzMessageService,
    private imageCompress: NgxImageCompressService,
    private fileFacade: FileFacade
  ) {
    this.fileFacade.files$
      .pipe(takeWhile(() => this.active))
      .subscribe((files: Dictionary<IFile>) => {
        this.transferRxFile = files[this.transferRxFileName];
        this.files = files;
      });
  }

  ngOnInit(): void {
    if (this.answer.type === 'radio') {
      this.answer.options.map(option => {
        if (option.answer) {
          this.optionAnswers[option.key] = option.answer;
        }
      });
    }

    if (this.answer.type === 'transfer-rx') {
      const paramsArray = (
        this.data[this.answer.option1.resourceId] || ''
      ).split('/');
      const customerId = paramsArray[paramsArray.length - 2];
      const fileName = paramsArray[paramsArray.length - 1];

      if (customerId && fileName) {
        this.fileFacade.getFile(customerId, fileName);
        this.transferRxFileName = fileName.replace(/%3A/g, ':');
      }
    }

    if (this.answer.type === 'esign') {
      const [org, customerId, fileName] = this.data?.answer?.key?.split('/');
      if (customerId && fileName) {
        this.fileFacade.getFile(customerId, fileName);
      }
    }

    if (this.question?.type === 'file_upload') {
      this.images = (this.question.answer || []).map(a => ({
        name: a.resourceId,
        path:
          typeof this.allAnswers[a.resourceId] === 'string'
            ? this.allAnswers[a.resourceId]
            : this.allAnswers[a.resourceId][a.resourceId],
        loading: true
      }));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      if (this.data === undefined) {
        this.data = {};
      }
    }
  }

  ngOnDestroy(): void {
    this.active = false;
  }

  get answerName() {
    let defaultName = 'defaultName';

    if (this.answer.type === 'text') defaultName = 'description';
    if (this.answer.type === 'number') defaultName = 'value';
    if (this.answer.type === 'date') defaultName = 'date';
    if (this.answer.type === 'consent-check') defaultName = 'accept';

    return this.answer.name || defaultName;
  }

  get isAccept() {
    return !this.answer.required || this.data['accept'] ? 'true' : '';
  }

  get uncheckAnswer() {
    const option =
      this.answer.type === 'multi_select'
        ? this.answer.options.filter(
          (opt: { key: string; value: string; uncheckOthers?: boolean }) =>
            opt.uncheckOthers
        )[0]
        : null;

    return option ? option.key : null;
  }

  get brandName() {
    const brand = this.brand?.split('-')[0];
    return brand?.charAt(0).toUpperCase() + brand?.slice(1);
  }

  change(value, data: any, key: string, answer): void {
    const answerKeys = answer.options.map(option => option.key);

    data[key] = value;
    if (data[key]) {
      if (key === this.uncheckAnswer || this.answer.type === 'single_select') {
        for (const a in data) {
          if (
            a !== key &&
            typeof data[a] === 'boolean' &&
            answerKeys.includes(a)
          ) {
            delete data[a];
          }
        }
      } else {
        delete data[this.uncheckAnswer];
      }
    } else {
      delete data[key];
    }
  }

  async processFile(resourceId: string, imageInput: any): Promise<void> {
    const selectedfile: File = imageInput.files[0];
    // if (selectedfile.size > 5000000) {
    // IMPORTANT: File limit has been increased to 15MB
    //   this.message.warning('Image can\'t be larger than 15MB');
    //   return;
    // }

    const reader = new FileReader();
    reader.addEventListener('load', (event: any) => {
      if (!!event.target.result && event.target.result != null) {
        this.compressFile(event.target.result.toString()).then(compressed => {
          this.data[resourceId] = compressed;
        });
      }
    });

    reader.readAsDataURL(selectedfile);
  }

  async compressFile(original: string): Promise<string> {
    try {
      const size = this.imageCompress.byteCount(original) / 1024;
      if (size < 2000) {
        // no need to compress
        return original;
      }

      const compressed = await this.imageCompress.compressFile(
        original,
        DOC_ORIENTATION.Up,
        50,
        80
      );
      console.warn(
        'Size in bytes is now:',
        this.imageCompress.byteCount(compressed) / 1024
      );
      return compressed;
    } catch (error) {
      console.error(error);
      this.message.error(error.message);
    }
  }

  addMoreImage(question): void {
    if (question.imageCount === undefined) {
      question.imageCount = 0;
    }

    if (question.imageCount < this.answer.option.limit) {
      question.imageCount++;
    }
  }

  removeImageCard(question): void {
    if (question.imageCount > 0) {
      question.imageCount--;
    }
  }

  /**
   * Functions for text_list
   */

  addItem(question): void {
    if (question.list === undefined) {
      question.list = [];
    }

    const length = question.list ? question.list.length : 0;
    if (length > 0) {
      const lastVal = question.list[length - 1].name;
      if (lastVal === '') {
        return;
      }
    }
    question.list.push({ name: '' });
    question.isNone = false;
  }

  removeItem(index: number, question): void {
    if (question.list.length > 0) {
      question.list.splice(index, 1);
    }

    if (!question.list.length) {
      question.isNone = true;
    }
  }

  checkCondition(cond: { key: string; operator: 'eq' | 'ne'; value: string }) {
    const { key, operator, value } = cond;
    const v = get(this.answers, key, '');
    if (operator == 'eq' && v === value) {
      return true;
    } else if (operator == 'ne' && v !== value) {
      return true;
    }
    return false;
  }

  get measureAnswerValue() {
    const selectedUnit = this.data[this.answer.unit.name];
    return !this.answer.value.placeholder
      ? {
        ...this.answer.value,
        placeholder:
          selectedUnit === 'feetIn'
            ? 'feet'
            : selectedUnit
              ? selectedUnit
              : this.answer.value.name
      }
      : this.answer.value;
  }

  processImage(name: string, imageInput: any) {
    const file: File = imageInput.files[0];

    const reader = new FileReader();
    reader.addEventListener('load', (event: any) => {
      if (!!event.target.result && event.target.result != null) {
        this.compressFile(event.target.result.toString()).then(
          async compressed => {
            const fileItem: IFile = {
              name,
              buffer: compressed,
              encoding: 'base64',
              mimetype: file.type
            };

            this.fileFacade.uploadFile(this.customerId, fileItem, undefined, (file: any) => {
              const url = file?.url || file?.location;
              this.images = this.images.map(image => {
                if (image.name === name) {
                  return { ...image, path: url };
                }
                return image;
              });

              this.allAnswers[name] = url;
            });
          }
        );
      }
    });

    reader.readAsDataURL(file);
  }
}
